import dynamic from 'next/dynamic';

import type { InspirationPostsSectionProps } from 'src/components/sections/InspirationPostsSection/InspirationPostsSection';
import { getInspirationPostsForTerm } from 'src/content/inspiration/getInspirationPostsForTerm';
import { toInspirationCardProps } from 'src/content/inspiration/toInspirationCardProps';
import { CATEGORY_PREVIEW_SIZE } from 'src/content/inspiration/toInspirationHubProps';
import { fetchInspirationLibrary } from 'src/services/contentstack/fetchInspirationLibrary';
import {
  INSPIRATION_CATEGORY_TAXONOMY_UID,
  INSPIRATION_OCCASION_TAXONOMY_UID,
} from 'src/services/contentstack/fetchInspirationTermNames';
import type { SectionRenderer, UISectionData } from 'src/types/Section';
import type { TransformerContext } from 'src/types/Transformer';
import { lazyLoadImages } from 'src/utils/images';
import { getInspirationCategoryPath, INSPIRATION_HUB_PATH } from 'src/utils/inspirationUrls';
import type { InspirationPostsContentSection } from './types';

export const DEFAULT_INSPIRATION_POSTS_HEADING = 'Inspiration';

/* istanbul ignore next */
export const renderer: SectionRenderer<InspirationPostsSectionProps> =
  dynamic<InspirationPostsSectionProps>(() =>
    import('src/components/sections/InspirationPostsSection/InspirationPostsSection').then(
      (mod) => mod.InspirationPostsSection,
    ),
  );

/** Taxonomies the block reads from, in order of preference. */
const POSTS_TAXONOMY_UIDS = [INSPIRATION_OCCASION_TAXONOMY_UID, INSPIRATION_CATEGORY_TAXONOMY_UID];

/**
 * The latest posts in the page's blog occasion, or in its blog category when
 * the occasion has none. Null, so the block renders nothing, when neither
 * term is set or has posts. Occasions have no page of their own, so they
 * link on to the hub.
 */
export const transformer = async (
  { _metadata, inspiration_posts_heading }: InspirationPostsContentSection,
  { pageMetadata, pageTerms, positionMetadata }: TransformerContext,
): Promise<UISectionData<InspirationPostsSectionProps> | null> => {
  const terms = POSTS_TAXONOMY_UIDS.flatMap(
    (uid) => pageTerms.find(({ taxonomyUid }) => taxonomyUid === uid) ?? [],
  );

  if (terms.length === 0) {
    return null;
  }

  const library = await fetchInspirationLibrary(pageMetadata.isPreview);
  const [match] = terms.flatMap((term) => {
    const posts = getInspirationPostsForTerm(library, term, CATEGORY_PREVIEW_SIZE);

    return posts.length > 0 ? [{ posts, term }] : [];
  });
  console.log('match', match);

  if (!match) {
    return null;
  }

  return {
    uid: _metadata.uid,
    props: {
      heading: inspiration_posts_heading?.trim() || DEFAULT_INSPIRATION_POSTS_HEADING,
      href:
        match.term.taxonomyUid === INSPIRATION_CATEGORY_TAXONOMY_UID
          ? getInspirationCategoryPath(match.term.termUid)
          : INSPIRATION_HUB_PATH,
      posts: toInspirationCardProps(match.posts, {
        eagerImageCount: lazyLoadImages(positionMetadata.position) ? 0 : 1,
      }),
    },
  };
};
